/** Feedback 1 #2: the 48-hour opt-out as a signed agreement. */
export type AgreementKind = 'wtr_opt_out' | 'wtr_withdrawal';
export type AgreementStatus = 'pending' | 'signed' | 'cancelled';
export const WTR_NOTICE_MIN_DAYS = 7;
export const WTR_NOTICE_MAX_DAYS = 92; // "up to three months"
export const AGREEMENT_LABEL: Record<AgreementKind, string> = { wtr_opt_out: '48-hour opt-out agreement', wtr_withdrawal: 'Withdrawal of 48-hour opt-out' };

export interface Agreement {
  id: string;
  user_id: string;
  kind: AgreementKind;
  template_version: number;
  status: AgreementStatus;
  message: string | null;
  requested_by: string | null;
  requested_by_name: string | null;
  requested_at: string;
  signed_at: string | null;
  signed_name: string | null;
  signed_on: string | null;
  signature_method: 'typed' | 'drawn' | null;
  notice_days: number | null;
  effective_on: string | null;
  document_sha256: string | null;
  cancelled_at: string | null;
}

export interface AgreementTemplate { kind: AgreementKind; version: number; title: string; body: string }

/** What the opt-out means today, for badges and the rules engine. */
export type WtrState = 'none' | 'opted_out' | 'legacy' | 'withdrawing' | 'withdrawn';
export interface WtrStatus {
  state: WtrState;
  /** effective right now (what the 48h average check uses) */
  opted_out: boolean;
  signed_on: string | null;
  ends_on: string | null;
  notice_days: number;
  pending: Agreement | null;
  agreements: Agreement[];
}

export function wtrState(e: { wtr_opt_out: boolean; wtr_opt_out_source?: 'legacy' | 'signed' | null; wtr_opt_out_ends_on?: string | null }, today: string): WtrState {
  if (!e.wtr_opt_out) return 'none';
  if (e.wtr_opt_out_ends_on) return e.wtr_opt_out_ends_on > today ? 'withdrawing' : 'withdrawn';
  return e.wtr_opt_out_source === 'legacy' ? 'legacy' : 'opted_out';
}

export const WTR_STATE_LABEL: Record<WtrState, string> = {
  none: 'Not opted out',
  opted_out: 'Opted out (signed)',
  legacy: 'Opted out - legacy, signature required',
  withdrawing: 'Withdrawing',
  withdrawn: 'Not opted out (withdrawn)',
};

export const noticeLabel = (days: number) => (days % 7 === 0 && days >= 14 ? `${days / 7} weeks’` : `${days} days’`);

/** Fill {name} {employer} {notice} {effective_on}; unknown placeholders are left as typed. */
export function fillAgreement(body: string, vars: { name: string; employer: string; notice_days: number; effective_on?: string | null }): string {
  const map: Record<string, string> = { name: vars.name, employer: vars.employer, notice: noticeLabel(vars.notice_days), effective_on: vars.effective_on ?? '' };
  return body.replace(/\{(name|employer|notice|effective_on)\}/g, (_, k: string) => map[k]);
}

/** Typed name (must match the person's name) or a drawn signature (the printed name is then their name). */
export function validateSignature(s: { typed_name?: string | null; method: 'typed' | 'drawn'; drawn_png?: string | null }, expectedName: string): string | null {
  const norm = (x: string) => x.trim().replace(/\s+/g, ' ').toLowerCase();
  if (s.method === 'typed') {
    const typed = (s.typed_name ?? '').trim();
    if (typed.length < 2) return 'Type your full name to sign';
    if (norm(typed) !== norm(expectedName)) return `Type your name exactly as it appears: ${expectedName}`;
    return null;
  }
  if (!(s.drawn_png && /^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(s.drawn_png))) return 'Draw your signature, or switch to a typed signature';
  if (s.drawn_png.length > 300_000) return 'That signature image is too large';
  return null;
}

export interface SignatureInput { method: 'typed' | 'drawn'; typed_name?: string; drawn_png?: string; date: string }
export interface AgreementPreview { agreement: Agreement; title: string; text: string; signer_name: string }
